import { callAct, isOk, statusOf } from './apiConfig';

/*
 * publicBusiness — the PUBLIC (no-auth) customer booking API for /b/:slug. Uses the
 * skipToken acts getPublicBusiness / publicBooking; the business is resolved from the slug
 * server-side (the client never sends a businessID/token).
 */

// Fetch a business's public page data by slug. Returns the business object or null.
export async function getPublicBusiness(slug) {
    try {
        const envelope = await callAct('getPublicBusiness', { slug });
        if (isOk(envelope) && envelope.data && envelope.data.name) {
            const d = envelope.data;
            return {
                slug: d.slug,
                name: d.name,
                city: d.city || '',
                phone: d.phone || '',
                currency: d.currency || 'ILS',
                categories: (d.categories || []).map((c) => c.name).filter(Boolean),
                menu: d.menu || [],           // [{category,name,description,price}]
                gallery: d.gallery || [],     // [{filePath,caption,isCover}]
                hours: d.hours || [],         // [{dayInWeek,startTime,endTime,serviceName}]
                areas: (d.areas || []).map((a) => ({ id: a.ID ?? a.id, name: a.name })),
            };
        }
    } catch (e) { /* ignore */ }
    return null;
}

// Fetch REAL availability for a business+date (public, no-auth). Returns the raw
// TimeSlots array from the backend on success, or [] on any error/non-OK. Never throws.
// Response data shape: { TimeSlots: [ { Time, Areas: { "Area<ID>": { Value, Capacities:
// { "Capacity<N>": { quantity, Value } } } } } ] }.
export async function getPublicAvailability({ slug, date, countryCode = 'IL' }) {
    try {
        const envelope = await callAct('publicAvailableTimes', { slug, startingDate: date, countryCode });
        if (isOk(envelope) && envelope.data && Array.isArray(envelope.data.TimeSlots)) {
            return envelope.data.TimeSlots;
        }
    } catch (e) { /* ignore */ }
    return [];
}

// Fetch the PUBLIC businesses directory (no auth). Returns { ok, data } where data is an
// array of { slug, name, city, cover, categories }. ok=false signals a real load failure
// (so the UI can show an error state vs an genuinely-empty directory). Never throws.
export async function listBusinesses() {
    try {
        const envelope = await callAct('publicBusinessList', {});
        if (isOk(envelope) && Array.isArray(envelope.data)) {
            return { ok: true, data: envelope.data };
        }
    } catch (e) { /* ignore */ }
    return { ok: false, data: [] };
}

// Submit a booking. Returns { ok, id, rateLimited }.
export async function publicBooking({ slug, date, startTime, numberOfGuests, guestName, guestPhone, area, guestNotes, marketingConsent }) {
    try {
        const envelope = await callAct('publicBooking', {
            slug, date, startTime, numberOfGuests, guestName, guestPhone,
            ...(area ? { area } : {}),
            ...(guestNotes ? { guestNotes } : {}),
            marketingConsent: marketingConsent ? 1 : 0, // separate opt-in (default 0)
        });
        return { ok: isOk(envelope), id: envelope?.data?.ID, manageToken: envelope?.data?.manageToken, rateLimited: statusOf(envelope) === 429 };
    } catch (e) { return { ok: false }; }
}

// ── PUBLIC SELF-MANAGE (customer /r/:token link, no auth). The token is the only selector;
// the server scopes strictly to that one reservation and never exposes any other.

// Fetch one reservation by its secret token. Returns the customer-safe reservation object
// { date, startTime, numberOfGuests, guestName, status, liveStatus, businessName, businessSlug }
// or null (invalid token / not found / any error). Never throws.
export async function getReservationByToken(token) {
    try {
        const envelope = await callAct('publicReservationByToken', { token });
        if (isOk(envelope) && envelope.data && envelope.data.date) {
            return envelope.data;
        }
    } catch (e) { /* ignore */ }
    return null;
}

// Cancel one reservation by its secret token. Returns { ok }. Idempotent-safe server-side.
export async function cancelReservationByToken(token) {
    try {
        const envelope = await callAct('publicReservationCancel', { token });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

// ── PUBLIC CUSTOMER SELF-SERVICE (/my — phone + SMS-OTP, NO account). The phone is the sole
// scope; the server proves it via a one-time SMS code, then returns ONLY that phone's future
// reservations. Both calls mirror the existing transport and NEVER throw.

// Step 1 — request an SMS code for this phone. The server is anti-enumeration: it ALWAYS returns
// 100 (and only actually sends an SMS if the phone has reservations), so we always resolve
// { ok: true } on a successful round-trip. { ok: false } only on a transport/validation error.
export async function sendCustomerOtp(phone) {
    try {
        const envelope = await callAct('sendCustomerOtp', { phone });
        return { ok: isOk(envelope) };
    } catch (e) { return { ok: false }; }
}

// Step 2 — verify the code and fetch this phone's future reservations. Returns
// { ok, rows } — rows is the reservation list on success; { ok: false } on a wrong/expired code
// (313) or any error. Each row: { businessName, businessSlug, date, startTime, numberOfGuests,
// guestName, liveStatus, manageToken }.
export async function getCustomerReservations(phone, otpCode) {
    try {
        const envelope = await callAct('customerReservations', { phone, otpCode });
        if (isOk(envelope)) {
            const rows = (envelope.data && Array.isArray(envelope.data.reservations)) ? envelope.data.reservations : [];
            return { ok: true, rows };
        }
    } catch (e) { /* ignore */ }
    return { ok: false, rows: [] };
}

export default { getPublicBusiness, getPublicAvailability, listBusinesses, publicBooking, getReservationByToken, cancelReservationByToken, sendCustomerOtp, getCustomerReservations };
